import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Download, Smartphone, Monitor, Tablet, Check, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Detect iOS
    const ua = navigator.userAgent;
    setIsIOS(/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
  };

  const texts = {
    fr: {
      title: 'Installer Consentink',
      subtitle: 'Accédez à l\'application directement depuis votre écran d\'accueil',
      installed: 'Application déjà installée !',
      installBtn: 'Installer l\'application',
      iosTitle: 'Installation sur iOS',
      iosStep1: 'Appuyez sur le bouton Partager',
      iosStep2: 'Sélectionnez "Sur l\'écran d\'accueil"',
      iosStep3: 'Confirmez en appuyant sur "Ajouter"',
      features: [
        'Fonctionne hors ligne',
        'Accès rapide depuis l\'écran d\'accueil',
        'Interface plein écran',
        'Synchronisation automatique',
      ],
      back: 'Retour',
    },
    en: {
      title: 'Install Consentink',
      subtitle: 'Access the app directly from your home screen',
      installed: 'App already installed!',
      installBtn: 'Install App',
      iosTitle: 'Install on iOS',
      iosStep1: 'Tap the Share button',
      iosStep2: 'Select "Add to Home Screen"',
      iosStep3: 'Confirm by tapping "Add"',
      features: [
        'Works offline',
        'Quick access from home screen',
        'Full-screen interface',
        'Automatic sync',
      ],
      back: 'Back',
    },
    es: {
      title: 'Instalar Consentink',
      subtitle: 'Acceda a la aplicación directamente desde su pantalla de inicio',
      installed: '¡Aplicación ya instalada!',
      installBtn: 'Instalar aplicación',
      iosTitle: 'Instalación en iOS',
      iosStep1: 'Toque el botón Compartir',
      iosStep2: 'Seleccione "Añadir a pantalla de inicio"',
      iosStep3: 'Confirme tocando "Añadir"',
      features: [
        'Funciona sin conexión',
        'Acceso rápido desde la pantalla de inicio',
        'Interfaz en pantalla completa',
        'Sincronización automática',
      ],
      back: 'Volver',
    },
  };

  const t = texts[language];
  const platforms = [
    { icon: Smartphone, label: 'Mobile' },
    { icon: Tablet, label: 'Tablet' },
    { icon: Monitor, label: 'Desktop' },
  ];

  return (
    <div className="tablet-container pb-28 space-y-8">
      <div className="flex items-center gap-4 pt-6">
        <button onClick={() => navigate('/')} className="touch-button p-3 bg-secondary rounded-xl">
          <X size={24} />
        </button>
        <h1 className="section-title flex-1">{t.title}</h1>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-primary rounded-3xl p-8 text-primary-foreground text-center space-y-4"
      >
        <Download size={48} className="mx-auto" />
        <h2 className="text-2xl font-bold font-display">{t.title}</h2>
        <p className="text-primary-foreground/80 text-lg">{t.subtitle}</p>

        <div className="flex justify-center gap-6 pt-4">
          {platforms.map(({ icon: Icon, label }) => (
            <div key={label} className="flex flex-col items-center gap-2">
              <div className="w-14 h-14 rounded-2xl bg-primary-foreground/15 flex items-center justify-center">
                <Icon size={26} />
              </div>
              <span className="text-sm text-primary-foreground/70">{label}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {isInstalled ? (
        <div className="card-elevated p-8 text-center space-y-4">
          <div className="w-20 h-20 mx-auto rounded-full bg-[hsl(var(--success))]/10 flex items-center justify-center">
            <Check size={40} className="text-[hsl(var(--success))]" />
          </div>
          <p className="text-xl font-bold font-display">{t.installed}</p>
        </div>
      ) : isIOS ? (
        <div className="card-elevated p-6 space-y-4">
          <h3 className="text-xl font-bold font-display">{t.iosTitle}</h3>
          <div className="space-y-3">
            {[t.iosStep1, t.iosStep2, t.iosStep3].map((step, i) => (
              <div key={i} className="flex items-center gap-4 p-4 bg-secondary rounded-xl">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                  {i + 1}
                </div>
                <p className="text-base">{step}</p>
              </div>
            ))}
          </div>
        </div>
      ) : deferredPrompt ? (
        <button
          onClick={handleInstall}
          className="touch-button-lg w-full bg-primary text-primary-foreground rounded-2xl flex items-center justify-center gap-3"
        >
          <Download size={24} />
          {t.installBtn}
        </button>
      ) : (
        <div className="card-elevated p-6 text-center text-muted-foreground">
          <p className="text-base">
            {language === 'fr'
              ? 'Ouvrez cette page dans votre navigateur pour installer l\'application.'
              : language === 'es'
              ? 'Abra esta página en su navegador para instalar la aplicación.'
              : 'Open this page in your browser to install the app.'}
          </p>
        </div>
      )}

      {/* Features */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {t.features.map((feature, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 * i }}
            className="card-elevated p-5 flex items-center gap-3"
          >
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Check size={16} className="text-primary" />
            </div>
            <span className="text-base font-medium">{feature}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default InstallPage;
